// src/contexts/UserProfileContext.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Contexto global de perfil social.
//
// Qualquer componente pode chamar:
//   const { openProfile } = useUserProfile();
//   openProfile(uid);   ← abre o offcanvas com perfil desse uid
//
// Navegação interna (followers → perfil → ...) é gerenciada via stack.
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useState, useCallback } from "react";
import UserProfileOffcanvas from "../features/social/UserProfileOffcanvas.jsx";

// ─── tipos de frame no stack ──────────────────────────────────────────────────
// { type: "profile", uid }
// { type: "list",    uid, initialTab: "followers" | "following" }

const UserProfileCtx = createContext(null);

export function UserProfileProvider({ children }) {
  const [stack, setStack] = useState([]);

  // Abre um perfil do zero (reseta o stack — usado externamente)
  const openProfile = useCallback((uid) => {
    if (!uid) return;
    setStack([{ type: "profile", uid }]);
  }, []);

  // Empurra um novo frame no stack (usado internamente pelo offcanvas)
  const push = useCallback((frame) => {
    setStack((prev) => [...prev, frame]);
  }, []);

  // Volta um passo
  const pop = useCallback(() => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : []));
  }, []);

  // Fecha tudo
  const closeAll = useCallback(() => setStack([]), []);

  const isOpen  = stack.length > 0;
  const current = stack[stack.length - 1] ?? null;

  return (
    <UserProfileCtx.Provider value={{ openProfile }}>
      {children}
      <UserProfileOffcanvas
        show={isOpen}
        current={current}
        canGoBack={stack.length > 1}
        onBack={pop}
        onClose={closeAll}
        onPush={push}
      />
    </UserProfileCtx.Provider>
  );
}

export function useUserProfile() {
  const ctx = useContext(UserProfileCtx);
  if (!ctx) {
    // Retorno seguro se provider não estiver na árvore ainda
    return { openProfile: () => {} };
  }
  return ctx;
}